import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Percent as PercentIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import './CommissionManagementPage.css';

interface OperatorCommissionManagement {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  commissionRate: number; // Admin-set commission rate (read-only)
  maxAllowedPercentage: number; // Maximum percentage allowed by upline
  currentUsedPercentage: number; // What downline is currently using
  allocatedToDownlines: number;
  remainingCommission: number;
}

interface CommissionManagement {
  userId: number;
  username: string;
  fullName: string;
  operatorCommissions: OperatorCommissionManagement[];
}

interface UpdateCommissionRequest {
  operatorId: number;
  salesPercentage: number;
}

const CommissionManagementPage: React.FC = () => {
  const [commissionData, setCommissionData] = useState<CommissionManagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingOperator, setEditingOperator] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchCommissionData();
  }, []);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch both commission rates and sales percentages
      const [commissionResponse, salesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/user/my-commissions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/api/user/commission-management`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!commissionResponse.ok || !salesResponse.ok) {
        throw new Error('Failed to fetch commission data');
      }

      const commissionData = await commissionResponse.json();
      const salesData = await salesResponse.json();

      // Merge the data
      const mergedData = {
        userId: salesData.userId,
        username: salesData.username,
        fullName: salesData.fullName,
        operatorCommissions: commissionData.operatorCommissions.map((commission: any) => {
          const salesOperator = salesData.operatorCommissions.find((so: any) => so.operatorId === commission.operatorId);
          return {
            operatorId: commission.operatorId,
            operatorCode: commission.operatorCode,
            operatorName: commission.operatorName,
            commissionRate: commission.commissionPercentage, // From admin
            maxAllowedPercentage: salesOperator?.maxAllowedPercentage || 0, // Maximum allowed by upline
            currentUsedPercentage: salesOperator?.currentUsedPercentage || 0, // What downline is using
            allocatedToDownlines: salesOperator?.allocatedToDownlines || 0,
            remainingCommission: salesOperator?.remainingCommission || 0
          };
        })
      };

      setCommissionData(mergedData);
    } catch (error) {
      console.error('Error fetching commission data:', error);
      setError('Failed to load commission data');
      enqueueSnackbar('Failed to load commission data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCommission = (operator: OperatorCommissionManagement) => {
    setEditingOperator(operator.operatorId);
    setEditValue((operator.currentUsedPercentage * 100).toString());
  };

  const getMaxSalesPercentage = (operator: OperatorCommissionManagement) => {
    // The maximum is the percentage allowed by upline
    return operator.maxAllowedPercentage * 100;
  };

  const handleCancelEdit = () => {
    setEditingOperator(null);
    setEditValue('');
  };

  const handleSaveCommission = async (operatorId: number) => {
    try {
      setSaving(true);
      const salesPercentage = parseFloat(editValue) / 100;

      const operator = commissionData?.operatorCommissions.find(op => op.operatorId === operatorId);
      const maxPercentage = operator ? getMaxSalesPercentage(operator) / 100 : 1;

      if (isNaN(salesPercentage) || salesPercentage < 0 || salesPercentage > maxPercentage) {
        enqueueSnackbar(`Please enter a valid percentage between 0 and ${(maxPercentage * 100).toFixed(1)}%`, { variant: 'error' });
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/user/commission-management/update-commission`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operatorId,
          salesPercentage
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update commission');
      }

      enqueueSnackbar('Sales percentage updated successfully', { variant: 'success' });
      setEditingOperator(null);
      setEditValue('');
      await fetchCommissionData();
    } catch (error) {
      console.error('Error updating commission:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update sales percentage', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const getCommissionStatusColor = (remaining: number) => {
    if (remaining > 0.1) return 'success';
    if (remaining > 0.05) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!commissionData) {
    return (
      <Box p={3}>
        <Alert severity="info">No commission data available</Alert>
      </Box>
    );
  }

  return (
    <Box className="commission-management-page">
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <PercentIcon className="title-icon" />
          Sales Management
        </Typography>
        <Typography variant="body1" color="text.secondary" className="page-subtitle">
          Set your actual sales percentages for each operator within the limits set by your upline.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {commissionData.operatorCommissions.map((operator) => (
          <Grid size={{ xs: 12 }} key={operator.operatorId}>
            <Card className="operator-card">
              <CardContent>
                <Box className="operator-header">
                  <Box>
                    <Typography variant="h6" className="operator-name">
                      {operator.operatorName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Code: {operator.operatorCode}
                    </Typography>
                  </Box>
                  <Chip
                    label={`Commission Rate: ${formatPercentage(operator.commissionRate)}`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                <Grid container spacing={2} className="commission-grid">
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box className="commission-item">
                      <Typography variant="body2" color="text.secondary">
                        Commission Rate (Admin)
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatPercentage(operator.commissionRate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Read-only
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box className="commission-item">
                      <Typography variant="body2" color="text.secondary">
                        Max Allowed by Upline
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        {formatPercentage(operator.maxAllowedPercentage)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Maximum you can use
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box className="commission-item">
                      <Typography variant="body2" color="text.secondary">
                        Sales Taking
                      </Typography>
                      {editingOperator === operator.operatorId ? (
                        <Box className="edit-container">
                                <TextField
                                  size="small"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  inputProps={{ 
                                    min: 0, 
                                    max: getMaxSalesPercentage(operator), 
                                    step: 0.01 
                                  }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                  }}
                                  className="edit-input"
                                  helperText={`Max: ${getMaxSalesPercentage(operator).toFixed(1)}%`}
                                />
                          <Box className="edit-actions">
                            <IconButton
                              size="small"
                              onClick={() => handleSaveCommission(operator.operatorId)}
                              disabled={saving}
                              color="primary"
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              disabled={saving}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box className="commission-display">
                          <Typography variant="h6" color="secondary">
                            {formatPercentage(operator.currentUsedPercentage)}
                          </Typography>
                          <Tooltip title="Edit your sales taking percentage">
                            <IconButton
                              size="small"
                              onClick={() => handleEditCommission(operator)}
                              className="edit-button"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Box className="commission-summary">
                  <Typography variant="body2" color="text.secondary">
                    Commission Rate: {formatPercentage(operator.commissionRate)} | 
                    Max Allowed: {formatPercentage(operator.maxAllowedPercentage)} | 
                    Sales Taking: {formatPercentage(operator.currentUsedPercentage)} | 
                    Available for Downlines: {formatPercentage(operator.remainingCommission)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {commissionData.operatorCommissions.length === 0 && (
        <Box p={3}>
        <Alert severity="info">
          No operator commission data found. Please contact your administrator.
        </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CommissionManagementPage;
